import {
  SortDisposition,
  SortDispositionSchema,
} from "@redotech/redo-fulfillment-wms-common/schema/sort-disposition";
import * as z from "zod";

/**
 * Keep in sync with the `GradeMapping` schema and the `GradeAcceptance`
 * enum-source registration in
 * redo/backend/db/schema/src/team/RedoManagedReturnsSettings.ts.
 */
export const GradeAcceptance = {
  ACCEPT: "accept",
  REJECT: "reject",
  FLAG_FOR_REVIEW: "flag_for_review",
} as const;
export type GradeAcceptance =
  (typeof GradeAcceptance)[keyof typeof GradeAcceptance];

export const GradeMappingSchema = z.object({
  acceptance: z.enum(GradeAcceptance),
  gradeName: z.string(),
  restock: z.boolean(),
  /** When set, the grade routes here at every hub; when unset, routing derives from `restock` and the hub. */
  sortDisposition: SortDispositionSchema.nullish(),
});
export const GradeMappingsSchema = z.array(GradeMappingSchema);
export type GradeMapping = z.infer<typeof GradeMappingSchema>;

export const GRADE_ACCEPTANCE_BADGE_COLORS: Record<
  GradeAcceptance,
  "success" | "warning" | "error"
> = {
  [GradeAcceptance.ACCEPT]: "success",
  [GradeAcceptance.FLAG_FOR_REVIEW]: "warning",
  [GradeAcceptance.REJECT]: "error",
};

export const DEFAULT_GRADE_MAPPINGS: readonly GradeMapping[] = [
  { gradeName: "A", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "B", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "C", acceptance: GradeAcceptance.ACCEPT, restock: true },
  { gradeName: "D", acceptance: GradeAcceptance.REJECT, restock: false },
  { gradeName: "E", acceptance: GradeAcceptance.REJECT, restock: false },
];

export function findGradeMapping(
  gradeName: string,
  gradeMappings: readonly GradeMapping[],
): GradeMapping | undefined {
  return (
    gradeMappings.find((m) => m.gradeName === gradeName) ??
    DEFAULT_GRADE_MAPPINGS.find((m) => m.gradeName === gradeName)
  );
}

/** `mapping` is null when completion rejects the grade as unconfigured, so UIs must show it unset, not defaulted. */
export interface GradeAction {
  gradeName: string;
  mapping: GradeMapping | null;
}

export function resolveGradeActions(
  gradeNames: readonly string[],
  gradeMappings: readonly GradeMapping[],
): GradeAction[] {
  return gradeNames.map((gradeName) => ({
    gradeName,
    mapping: findGradeMapping(gradeName, gradeMappings) ?? null,
  }));
}

export function setGradeActionAcceptance(
  actions: readonly GradeAction[],
  gradeName: string,
  acceptance: GradeAcceptance,
): GradeAction[] {
  return actions.map((action) => {
    if (action.gradeName !== gradeName) {
      return action;
    }
    const rejected = acceptance === GradeAcceptance.REJECT;
    const sortDisposition = action.mapping?.sortDisposition;
    return {
      gradeName,
      mapping: {
        gradeName,
        acceptance,
        restock: !rejected && (action.mapping?.restock ?? false),
        ...(sortDisposition &&
        !(rejected && sortDisposition === SortDisposition.restock)
          ? { sortDisposition }
          : {}),
      },
    };
  });
}

export function setGradeActionRestock(
  actions: readonly GradeAction[],
  gradeName: string,
  restock: boolean,
): GradeAction[] {
  return actions.map((action) =>
    action.gradeName === gradeName && action.mapping
      ? { ...action, mapping: { ...action.mapping, restock } }
      : action,
  );
}

/** Keeps `restock` in step with the disposition for readers that still branch on the flag. */
export function setGradeActionSortDisposition(
  actions: readonly GradeAction[],
  gradeName: string,
  sortDisposition: SortDisposition,
): GradeAction[] {
  return actions.map((action) =>
    action.gradeName === gradeName && action.mapping
      ? {
          ...action,
          mapping: {
            ...action.mapping,
            sortDisposition,
            restock: sortDisposition === SortDisposition.restock,
          },
        }
      : action,
  );
}

/** Dispositions a grade with this acceptance may route to; rejected grades never restock. */
export function gradeSortDispositionChoices(
  acceptance: GradeAcceptance,
): SortDisposition[] {
  return Object.values(SortDisposition).filter(
    (d) =>
      acceptance !== GradeAcceptance.REJECT || d !== SortDisposition.restock,
  );
}

export function gradeMappingsFromActions(
  actions: readonly GradeAction[],
): GradeMapping[] {
  return actions.flatMap((action) => (action.mapping ? [action.mapping] : []));
}

/** A chosen sort disposition decides restocking; the `restock` flag only covers grades that have none yet. */
export function mappingRestocks(mapping: GradeMapping): boolean {
  return mapping.sortDisposition
    ? mapping.sortDisposition === SortDisposition.restock
    : mapping.restock;
}

export function gradeRestocks(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): boolean | null {
  if (gradeName === null) {
    return null;
  }
  const mapping = findGradeMapping(gradeName, gradeMappings);
  return mapping ? mappingRestocks(mapping) : null;
}

/**
 * The grade's raw `restock` flag, ignoring any chosen sort disposition: the
 * rule from before sort dispositions. Readers of an existing unit with no
 * stored `sort_disposition` use it, so a disposition configured after grading
 * reaches the unit only through the stored value.
 */
export function legacyGradeRestocks(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): boolean | null {
  if (gradeName === null) {
    return null;
  }
  return findGradeMapping(gradeName, gradeMappings)?.restock ?? null;
}

export function gradeSortDisposition(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): SortDisposition | null {
  if (gradeName === null) {
    return null;
  }
  return findGradeMapping(gradeName, gradeMappings)?.sortDisposition ?? null;
}

export function gradeDisposition(
  gradeName: string | null,
  gradeMappings: readonly GradeMapping[] = DEFAULT_GRADE_MAPPINGS,
): "restock" | null {
  return gradeRestocks(gradeName, gradeMappings) === true ? "restock" : null;
}

export const DEFAULT_WAREHOUSE_GRADE_NAMES: readonly string[] =
  DEFAULT_GRADE_MAPPINGS.map((m) => m.gradeName);

/** Custom grades when enabled (possibly empty), else the default A–E warehouse grades. */
export function resolveWarehouseGradeNames(params: {
  customGradesEnabled: boolean;
  gradeOptions: readonly string[] | null | undefined;
}): string[] {
  if (params.customGradesEnabled) {
    return [...(params.gradeOptions ?? [])];
  }
  return [...DEFAULT_WAREHOUSE_GRADE_NAMES];
}
